<template>
    <div class="email-broadcast-editor-page">
        <template v-if="!broadcastLoadError">
            <EmailBroadcastEditorHeader
                data-qa="email-broadcast-editor-header"
                :email="email"
                :disabled="!isInitialized"
                :loading="isLoading"
                :loading-send-test="loadingSendTest"
                :should-send-now="shouldSendNow"
                @back="$emit('back')"
                @send="sendBroadcast"
                @send-test="sendTestEmail"
            />

            <div v-if="isInitialized" class="page-content" data-qa="email-broadcast-editor-content">
                <div v-if="showErrors && errors.length > 0" class="error-container">
                    <DsInlineAlert
                        v-for="error of errors"
                        :key="error"
                        leading-icon
                        type="critical"
                    >
                        {{ error }}
                    </DsInlineAlert>
                </div>

                <div class="broadcast-steps">
                    <div
                        v-for="step in steps"
                        :key="step.title"
                        :data-qa="step.title"
                        class="broadcast-step"
                    >
                        <component
                            :is="step.component"
                            :active="isStepActive(step)"
                            :is-saving-broadcast="isSavingBroadcast"
                            :email="email"
                            :saved-subject="email[$options.STEPS.SUBJECT].subject"
                            :saved-preview-text="email[$options.STEPS.SUBJECT].previewText"
                            :html="email[$options.STEPS.CONTENT].htmlBody"
                            :has-content="hasContent"
                            :show-errors="isStepError(step)"
                            :content-risk-messages="contentRiskMessages"
                            :content-risk-quality="contentRiskQuality"
                            :content-risk-sendable="contentRiskSendable"
                            :fetch-content-risks="fetchContentRisks"
                            :fetch-subject-risks="fetchSubjectRisks"
                            :loading-subject-risk="loadingSubjectRisk"
                            :loading-content-risk="loadingContentRisk"
                            :subject-risk-messages="subjectRiskMessages"
                            :subject-risk-quality="subjectRiskQuality"
                            :subject-risk-sendable="subjectRiskSendable"
                            :show-verified-domains-new-keys-only-enabled="coreConfig.showVerifiedDomainsNewKeysOnlyEnabled"
                            :is-recalculating-recipient-statistics="isRecalculatingRecipientStatistics"
                            :wrap-sender-display-name-with-quotes-enabled="coreConfig.wrapSenderDisplayNameWithQuotesEnabled"
                            :merge-fields="mergeFields"
                            :saved-from="email[$options.STEPS.SENDER].from"
                            :saved-reply-to-address="email[$options.STEPS.SENDER].replyToAddress"
                            :merge-fields-in-from-address-enabled="coreConfig.mergeFieldsInFromAddressEnabled"
                            :schedule-date-fix-enabled="coreConfig.isScheduledEmailDateTimeFixEnabled"
                            is-max-classic
                            @load-preview="loadPreview"
                            @cancel="cancelEditStep"
                            @error="handleStepError(step, $event)"
                            @save="saveStep(step, $event)"
                        >
                            <template #title>
                                <div class="step-title">
                                    <div class="step-status-icon" :class="iconClass(step)">
                                        <DsIcon :name="stepIcon(step)" />
                                    </div>
                                    {{ $t(`email.broadcast.editor.sectionTitle.${step.title.toLowerCase()}`) }}
                                </div>
                            </template>

                            <template #action="{ actionText }">
                                <component
                                    :is="isStepNew(step) ? 'DsOutlineButton' : 'DsTextButton'"
                                    v-if="!isStepActive(step)"
                                    class="step-action"
                                    dense
                                    :disabled="!!activeStepTitle"
                                    @click="activateStep(step)"
                                >
                                    {{ getActionText(step, actionText) }}
                                </component>
                            </template>
                        </component>
                    </div>
                </div>
            </div>
        </template>

        <template v-else>
            <p class="broadcast-load-error-container">
                {{ $t('email.broadcast.editor.errors.loading') }}
            </p>
        </template>

        <UnlayerEmailBroadcastWizardModal
            v-if="isUnlayerWizardOpen"
            is-max-classic
            :email="email"
            :core-config="coreConfig"
            :content-risk-messages="contentRiskMessages"
            :content-risk-quality="contentRiskQuality"
            :loading-content-risk="loadingContentRisk"
            :content-risk-sendable="contentRiskSendable"
            :fetch-content-risks="fetchContentRisks"
            :external-template-id="email.templateId"
            @close="handleUnlayerWizardModalClose"
            @save="updateContentAndSave"
            @update-template="handleUpdateTemplate"
            @template-selected="onTemplateSelected"
        />

        <ContentRiskActions v-slot="props">
            <SendTestEmailModal
                v-bind="props"
                :is-open="isSendTestEmailModalOpen"
                :from-address="email[$options.STEPS.SENDER].from"
                :html-body="email[$options.STEPS.CONTENT].htmlBody"
                :subject="email[$options.STEPS.SUBJECT].subject"
                :preview-text="email[$options.STEPS.SUBJECT].previewText"
                :show-verified-domains-new-keys-only-enabled="coreConfig.showVerifiedDomainsNewKeysOnlyEnabled"
                :contact-search-enabled="coreConfig.contactSearchInTestEmailsEnabled"
                @close="isSendTestEmailModalOpen = false"
            />
        </ContentRiskActions>

        <UnlayerEmailBuilderFtuModal
            :is-open="isFtuModalOpen"
            @close="closeFtuModal"
        />

        <EmailBroadcastCanSpamModal
            v-if="!isCanSpamValid"
            :is-open="isCanSpamModalOpen"
            :company-profile="companyProfile"
            data-qa="email-broadcast-can-spam-modal"
            @close="handleCanSpamModalClose"
        />

        <DsConfirmDialog
            :is-open="isBroadcastNonEditable"
            destructive
            size="sm"
            :title="$t('email.broadcast.editor.errors.duplicate.title')"
            :message="$t('email.broadcast.editor.errors.duplicate.body')"
            :confirm-label="$t('global.close')"
            confirm-only
            @confirm="closeNonEditableDialog"
        />
    </div>
</template>

<script>
import clonedeep from 'lodash.clonedeep';
import EmailBroadcastCanSpamModal from './EmailBroadcastCanSpamModal';
import EmailBroadcastEditorHeader from './EmailBroadcastEditorHeader';
import UnlayerEmailBuilderFtuModal from './UnlayerEmailBuilderFtuModal';
import UnlayerEmailBroadcastWizardModal from './UnlayerEmailBroadcastWizardModal';
import SendTestEmailModal from '@/components/unlayer/sendTestEmail/SendTestEmailModal';
import {
    EmailBroadcastAudienceStep,
    EmailBroadcastContentStep,
    EmailBroadcastScheduleStep,
    EmailBroadcastSenderStep,
    EmailBroadcastSubjectStep,
} from './steps';
import {
    differenceById,
    getEmptyMarketingSettingsInformation,
    prepareDateTimePayload,
    isScheduleDatePast,
    validateBroadcast,
} from '../utils/emailBroadcast.util';
import { loadFtu, updateFtuItem } from '@/ftu.utils';
import { FTU_TYPES } from '@/ftu.constants';
import {
    createUnlayerBroadcastWithTemplate,
    getMarketingSettingsInformation,
    getUnlayerBroadcastWithTemplateById,
    sendBroadcast,
    unlayerBroadcastsAddRecipientCriteria,
    unlayerBroadcastsRemoveRecipientCriteria,
    updateUnlayerBroadcastWithTemplate,
    recalculateEmailBroadcastRecipientStatisticsById as recalculateRecipientStatistics,
    getBroadcastPreviewHtml,
} from '@/api/broadcasts/emailBroadcast';
import {
    BROADCAST_STATUS,
    REQUIRED_CAN_SPAM_FIELDS,
    CONTACT_TIME_ZONE_OPTION,
} from '../constants/emailBroadcast.constants';
import {
    STATUSES,
    STATUS_ICONS,
    STEPS,
    STEP_ERRORS,
} from '@/components/unlayer/constants/editor.constants';
import { createNewUnlayerEmail, validateUnlayerEmail } from '@/components/unlayer/unlayerEmail.util';
import { SOURCES } from '@/components/unlayer/constants/unlayer.constants';
import ContentRiskActions from '@/components/unlayer/ContentRiskActions.vue';
import { getMergeFields } from '@/api/campaigns/campaignUnlayerEmail';

const SEND_TEST_REQUIRED_STEPS = [STEPS.SUBJECT, STEPS.CONTENT];

export default {
    STEPS,

    name: 'EmailBroadcastEditorPage',

    components: {
        ContentRiskActions,
        EmailBroadcastCanSpamModal,
        EmailBroadcastEditorHeader,
        UnlayerEmailBroadcastWizardModal,
        UnlayerEmailBuilderFtuModal,
        SendTestEmailModal,
    },

    props: {
        listId: Number,
        fromContactListRedirect: Boolean,
        broadcastId: Number,
        coreConfig: Object,
        contentRiskMessages: {
            type: Array,
            default: () => [],
        },
        contentRiskQuality: String,
        contentRiskSendable: Boolean,
        fetchContentRisks: {
            type: Function,
            default: (x) => x,
        },
        fetchSubjectRisks: {
            type: Function,
            default: (x) => x,
        },
        loadingContentRisk: Boolean,
        loadingSubjectRisk: Boolean,
        subjectRiskMessages: {
            type: Array,
            default: () => [],
        },
        subjectRiskQuality: String,
        subjectRiskSendable: Boolean,
        showVerifiedDomainsNewKeysOnlyEnabled: Boolean,
    },

    data() {
        return {
            activeStepTitle: '',
            email: createNewUnlayerEmail(SOURCES.BROADCAST),
            broadcastStatus: STATUSES.READY,
            companyProfile: getEmptyMarketingSettingsInformation(),
            errors: [],
            isCanSpamModalOpen: false,
            isContentDirty: true,
            isFtuModalOpen: false,
            isInitialized: false,
            isSendTestEmailModalOpen: false,
            isRecalculatingRecipientStatistics: false,
            isUnlayerWizardOpen: false,
            lastSavedEmail: createNewUnlayerEmail(SOURCES.BROADCAST),
            previousToAddressType: '',
            showErrors: false,
            steps: [{
                component: EmailBroadcastSenderStep,
                status: STATUSES.COMPLETE,
                title: STEPS.SENDER,
            }, {
                component: EmailBroadcastSubjectStep,
                status: STATUSES.NEW,
                title: STEPS.SUBJECT,
            }, {
                component: EmailBroadcastAudienceStep,
                status: STATUSES.NEW,
                title: STEPS.AUDIENCE,
            }, {
                component: EmailBroadcastScheduleStep,
                status: STATUSES.COMPLETE,
                title: STEPS.SCHEDULE,
            }, {
                component: EmailBroadcastContentStep,
                status: STATUSES.NEW,
                title: STEPS.CONTENT,
            }],
            senderDkimError: false,
            loadingSendTest: false,
            mergeFields: [],
            isBroadcastNonEditable: false,
        };
    },

    async created() {
        const promises = [
            this.fetchMarketingSettingsInformation(),
            this.checkFtu(),
            this.fetchMergeFields(),
        ];

        if (this.broadcastId) {
            promises.push(this.loadBroadcast(this.broadcastId));
        } else if (this.listId && this.fromContactListRedirect) {
            promises.push(this.saveBroadcast(this.listId));
        }

        await Promise.all(promises);

        this.previousToAddressType = this.email[STEPS.AUDIENCE].toAddress;

        if (this.broadcastId) {
            await this.recalculateRecipientStatistics();
        }

        this.isInitialized = true;
    },

    computed: {
        hasEligibleRecipients() {
            const eligibleRecipients = this.email[STEPS.AUDIENCE].recipientStatistics?.eligibleRecipients;

            return eligibleRecipients == null || eligibleRecipients > 0;
        },

        isAnyStepActive() {
            return this.steps.map((step) => step.title).includes(this.activeStepTitle);
        },

        isLoading() {
            return [
                STATUSES.LOADING,
                STATUSES.SAVING,
                STATUSES.SENDING,
            ].includes(this.broadcastStatus) && !this.loadingSendTest;
        },

        isCanSpamValid() {
            return REQUIRED_CAN_SPAM_FIELDS.every((field) => this.companyProfile[field]);
        },

        shouldSendNow() {
            const { scheduleDate, timeZoneId, useContactTimeZone } = this.email[STEPS.SCHEDULE];

            return scheduleDate === null || isScheduleDatePast({ scheduleDate, timeZoneId, useContactTimeZone });
        },

        isSavingBroadcast() {
            return this.broadcastStatus === STATUSES.SAVING;
        },

        broadcastLoadError() {
            return this.broadcastStatus === STATUSES.ERROR_LOADING;
        },

        hasContent() {
            return validateUnlayerEmail(this.email, STEPS.CONTENT);
        },
    },

    methods: {
        async activateStep({ title }) {
            if (title === STEPS.CONTENT) {
                this.isUnlayerWizardOpen = true;
            } else if (this.steps.map((s) => s.title).includes(title)) {
                this.activeStepTitle = title;
            }
        },

        cancelEditStep() {
            this.activeStepTitle = '';
        },

        async checkFtu() {
            const ftu = await loadFtu(this.coreConfig.appId, this.coreConfig.userId);

            if (!ftu?.[FTU_TYPES.EMAIL_BROADCAST.UNLAYER]) {
                this.isFtuModalOpen = true;
            }
        },

        async closeFtuModal() {
            this.isFtuModalOpen = false;

            await updateFtuItem(this.coreConfig.appId, this.coreConfig.userId, FTU_TYPES.EMAIL_BROADCAST.UNLAYER, true);
        },

        errorAdd(message) {
            if (!this.errors.includes(message)) {
                this.errors.push(message);
            }
        },

        errorRemoveAll() {
            this.errors = [];
        },

        async fetchMarketingSettingsInformation() {
            try {
                this.companyProfile = await getMarketingSettingsInformation();
            } catch (e) {
                this.$error({ message: this.$t('email.broadcast.editor.errors.businessProfile'), bottom: true });
            }
        },

        getActionText({ status }, text) {
            if (status === STATUSES.NEW) {
                return text;
            }

            return this.$t('global.edit');
        },

        handleUpdateTemplate(t) {
            this.email[STEPS.CONTENT].htmlBody = t.htmlBody;
            this.email[STEPS.CONTENT].design = t.design;
        },

        async handleCanSpamModalClose(profile) {
            this.isCanSpamModalOpen = false;

            if (profile) {
                this.companyProfile = profile;
            }
        },

        handleStepError(step, errorCode) {
            this.showErrors = true;

            this.setStepStatusByTitle(step.title, STATUSES.ERROR);

            const errorMessage = (() => {
                switch (errorCode) {
                case STEP_ERRORS.SENDER_NOT_FOUND:
                    return this.$t('email.broadcast.editor.errors.senderNotFound');
                case STEP_ERRORS.SENDER_DKIM_ERROR:
                    return this.$t('email.broadcast.editor.errors.senderDkimError');
                case STEP_ERRORS.ERROR_FETCHING_CUSTOM_DOMAINS:
                    return this.$t('email.broadcast.editor.errors.fetchingCustomDomains');
                case STEP_ERRORS.ERROR_FETCHING_USERS:
                    return this.$t('email.broadcast.editor.errors.fetchingUsers');
                case STEP_ERRORS.ERROR_FETCHING_LOCALIZATION_INFO:
                    return this.$t('email.broadcast.editor.errors.fetchingLocalizationInfo');
                case STEP_ERRORS.NO_ELIGIBLE_RECIPIENTS:
                    return this.$t('email.broadcast.editor.errors.noEligibleRecipients');
                case STEP_ERRORS.SUBJECT_OR_PREVIEW_LIMIT_EXCEEDED:
                    return this.$t('email.broadcast.editor.errors.step.subject');
                default:
                    return errorCode;
                }
            })();

            this.errorAdd(errorMessage);

            if (step.title === STEPS.SENDER && errorCode === STEP_ERRORS.SENDER_DKIM_ERROR) {
                this.senderDkimError = true;
            }

            if (this.errors.length === 1) {
                this.activateStep(step);
            }
        },

        iconClass(step) {
            if (this.isStepError(step)) {
                return STATUS_ICONS.error;
            }

            if (this.isStepActive(step)) {
                return STATUS_ICONS.active;
            }

            return STATUS_ICONS[step.status] ?? '';
        },

        isStepActive({ title }) {
            return title === this.activeStepTitle;
        },

        isStepError(step) {
            if (step.title === STEPS.AUDIENCE) {
                let error = (step.status === STATUSES.ERROR);

                if (step.status !== STATUSES.NEW) {
                    error = error || !this.hasEligibleRecipients;
                }

                return error;
            }

            return this.showErrors
                && (step.status === STATUSES.ERROR
                || step.status === STATUSES.ERROR_SENDING_NO_RECIPIENTS
                || step.status === STATUSES.ERROR_HAS_NOT_FINISHED_EDITING
                || (step.title === STEPS.CONTENT && !this.contentRiskSendable)
                || (step.title === STEPS.SENDER && this.senderDkimError));
        },

        isStepNew({ status }) {
            return status === STATUSES.NEW;
        },

        async loadBroadcast(broadcastId) {
            if (this.broadcastStatus === STATUSES.LOADING) {
                return;
            }

            try {
                this.broadcastStatus = STATUSES.LOADING;
                this.email = await getUnlayerBroadcastWithTemplateById(broadcastId);

                this.validateBroadcastState();

                this.lastSavedEmail = clonedeep(this.email);

                this.broadcastStatus = STATUSES.READY;

                if (broadcastId) {
                    this.setCompletedSteps();
                }
            } catch (err) {
                this.broadcastStatus = STATUSES.ERROR_LOADING;
            }
        },

        async saveBroadcast(listId) {
            this.errorRemoveAll();

            this.broadcastStatus = STATUSES.SAVING;

            try {
                if (!this.email.id) {
                    const newBroadcast = await createUnlayerBroadcastWithTemplate(listId);

                    this.email.id = newBroadcast.id;
                    this.email.templateId = newBroadcast.templateId;
                    if (this.email.templateTitle === undefined) this.email.templateTitle = newBroadcast.templateTitle;
                }

                await this.saveRecipientsToBroadcast();

                await updateUnlayerBroadcastWithTemplate(this.email);
                await this.loadBroadcast(this.email.id);

                this.$emit('update-broadcast-id', this.email.id);

                this.email.lastEdit = new Date().toString();
            } catch (e) {
                if (e.response?.status === 409) {
                    this.errorRemoveAll();
                    this.isBroadcastNonEditable = true;

                    return;
                }
                this.broadcastStatus = STATUSES.ERROR_SAVING;
                this.errorAdd(this.$t('email.broadcast.editor.errors.saving'));
                this.showErrors = true;
            }
        },

        async loadPreview({ resolve, reject }) {
            try {
                const result = await getBroadcastPreviewHtml({ broadcastId: this.email.id });

                resolve(result);
            } catch (e) {
                reject(e);
            }
        },

        async saveRecipientsToBroadcast() {
            const updatedRecipients = this.email[STEPS.AUDIENCE].recipients;
            const existingRecipients = this.lastSavedEmail[STEPS.AUDIENCE].recipients;

            const toRecipientPayload = ({
                id,
                category,
                label,
                name,
                type,
            }) => {
                return {
                    id: `${id}`,
                    category: category || type,
                    label: label || name,
                };
            };

            const addedRecipients = differenceById(updatedRecipients, existingRecipients);
            const removedRecipients = differenceById(existingRecipients, updatedRecipients);

            // due to the way the BE currently processes these requests, the below calls must be sequential
            for (const addedRecipient of addedRecipients) {
                /* eslint-disable-next-line no-await-in-loop */
                await unlayerBroadcastsAddRecipientCriteria({
                    broadcastId: this.email.id,
                    recipient: toRecipientPayload(addedRecipient),
                });
            }

            for (const removedRecipient of removedRecipients) {
                /* eslint-disable-next-line no-await-in-loop */
                await unlayerBroadcastsRemoveRecipientCriteria({
                    broadcastId: this.email.id,
                    recipient: toRecipientPayload(removedRecipient),
                });
            }
        },

        async saveStep(step, data) {
            if (step.title === STEPS.SENDER) {
                this.senderDkimError = false;
            }

            if (step.title === STEPS.AUDIENCE) {
                this.isRecalculatingRecipientStatistics = true;
            }

            this.activeStepTitle = '';

            this.updateBroadcastForStep(step, data);

            await this.saveBroadcast();

            if (step.title === STEPS.AUDIENCE
                    && this.broadcastId
                    && this.previousToAddressType !== data?.toAddress
            ) {
                await this.recalculateRecipientStatistics();

                this.previousToAddressType = data.toAddress;
            }

            this.isRecalculatingRecipientStatistics = false;

            this.setStepValidation(step);

            if (this.broadcastStatus === STATUSES.ERROR_SAVING) {
                return;
            }

            if (this.showErrors) {
                await this.validate();
            }
        },

        async sendBroadcast() {
            this.errorRemoveAll();

            this.showErrors = true;

            this.broadcastStatus = STATUSES.SENDING;

            const isValid = await this.validate();

            if (!isValid) {
                this.broadcastStatus = STATUSES.ERROR;

                return;
            }

            if (!this.isCanSpamValid) {
                this.isCanSpamModalOpen = true;
                this.broadcastStatus = STATUSES.READY;

                return;
            }

            try {
                const dateTime = this.coreConfig.isScheduledEmailDateTimeFixEnabled
                    ? this.email[STEPS.SCHEDULE].scheduleDateWithoutTimeZone
                    : prepareDateTimePayload(this.email[STEPS.SCHEDULE].scheduleDate);

                await sendBroadcast({
                    broadcastId: this.email.id,
                    schedule: !this.shouldSendNow ? {
                        timeZoneId: this.email[STEPS.SCHEDULE].useContactTimeZone ? CONTACT_TIME_ZONE_OPTION : this.email[STEPS.SCHEDULE].timeZoneId,
                        dateTime,
                    } : null,
                });

                this.$toast({
                    message: this.$t(this.shouldSendNow
                        ? 'email.broadcast.editor.send.success'
                        : 'email.broadcast.editor.schedule.success'),
                });
                this.$emit('back', this.email.id);
            } catch (e) {
                if (e.response?.status === 409) {
                    this.isBroadcastNonEditable = true;
                    this.errorRemoveAll();
                    this.broadcastStatus = STATUSES.READY;

                    return;
                }
                this.errorAdd(this.$t('email.broadcast.editor.errors.sending'));
                this.broadcastStatus = STATUSES.ERROR_SENDING;
            }
        },

        async sendTestEmail() {
            this.loadingSendTest = true;

            await this.loadBroadcastStatus();

            if (this.isBroadcastNonEditable) {
                this.loadingSendTest = false;

                return;
            }
            this.showErrors = true;

            const isValid = await this.validate({ test: true });

            this.loadingSendTest = false;

            if (!isValid) return;

            this.isSendTestEmailModalOpen = true;
        },

        setCompletedSteps() {
            for (const { title } of this.steps) {
                if (validateBroadcast(this.email, title)) {
                    this.setStepStatusByTitle(title, STATUSES.COMPLETE);
                }
            }
        },

        setStepStatusByTitle(stepTitle, status) {
            const step = this.steps.find(({ title }) => title === stepTitle);

            if (step) step.status = status;
        },

        setStepValidation({ title }) {
            let isValid = validateBroadcast(this.email, title);

            if (title === STEPS.SENDER) {
                isValid = isValid && !this.senderDkimError;
            }

            if (title === STEPS.SUBJECT) {
                isValid = isValid && this.subjectRiskSendable;
            }

            if (title === STEPS.AUDIENCE) {
                isValid = isValid && this.hasEligibleRecipients;
            }

            if (title === STEPS.CONTENT) {
                isValid = isValid && this.contentRiskSendable;
                this.isContentDirty = false;
            }

            const status = isValid ? STATUSES.COMPLETE : STATUSES.ERROR;

            this.setStepStatusByTitle(title, status);

            return status;
        },

        stepIcon(step) {
            if (this.isStepError(step)) {
                return 'alert-triangle-fill';
            }

            return 'check';
        },

        updateBroadcastForStep({ title }, data) {
            const { email } = this;
            const stepData = email[title];

            const updaters = {
                [STEPS.AUDIENCE]() {
                    stepData.recipients = data.recipients;
                    stepData.toAddress = data.toAddress;
                },

                [STEPS.CONTENT]() {
                    stepData.design = data.design;
                    stepData.htmlBody = data.htmlBody;
                    email.templateTitle = data.title;
                },

                [STEPS.SCHEDULE]() {
                    stepData.scheduleDate = data.scheduleDate;
                    stepData.scheduleDateWithoutTimeZone = data.scheduleDateWithoutTimeZone;
                    stepData.timeZoneId = data.timeZoneId;
                    stepData.useContactTimeZone = data.useContactTimeZone;
                },

                [STEPS.SENDER]() {
                    stepData.from = data.from;
                    stepData.replyToAddress = data.replyToAddress;
                },

                [STEPS.SUBJECT]() {
                    stepData.subject = data.subject;
                    stepData.previewText = data.previewText;
                },
            };

            const updater = updaters[title];

            if (updater) updater();

            if (title === STEPS.CONTENT) {
                this.isContentDirty = true;
            }
        },

        async onTemplateSelected(template) {
            if (template.name) template.title = template.name;
            await this.updateContentAndSave(template)
        },

        async updateContentAndSave(template) {
            this.updateBroadcastForStep({ title: STEPS.CONTENT }, template);

            await this.saveBroadcast();

            if (this.showErrors) {
                await this.validate();
            }
        },

        async validate({ test } = {}) {
            const promises = [];

            if (this.isContentDirty) {
                promises.push(this.fetchContentRisks(this.email[STEPS.CONTENT].htmlBody));
            }

            if (!this.subjectRiskSendable) {
                promises.push(this.fetchSubjectRisks(this.email[STEPS.SUBJECT].subject));
            }
            await Promise.all(promises);

            let errorCount = 0;
            let invalidStep = null;
            const steps = test
                ? this.steps.filter(({ title }) => SEND_TEST_REQUIRED_STEPS.includes(title))
                : this.steps;

            steps.forEach((currStep) => {
                if (this.setStepValidation(currStep) !== STATUSES.COMPLETE) {
                    if (!invalidStep) {
                        invalidStep = currStep;
                    }
                    errorCount++;
                }
            });

            if (errorCount > 1) {
                this.errorAdd(this.$t('email.broadcast.editor.errors.multiple'));

                return false;
            }

            if (errorCount === 1) {
                if (invalidStep.title === STEPS.AUDIENCE && !this.hasEligibleRecipients) {
                    this.errorAdd(this.$t('email.broadcast.editor.errors.noEligibleRecipients'));
                } else if (invalidStep.title === STEPS.CONTENT && !this.contentRiskSendable) {
                    this.errorAdd(this.$t('email.broadcast.editor.errors.contentRisk'));
                } else if (invalidStep.title === STEPS.SENDER && this.senderDkimError) {
                    this.errorAdd(this.$t('email.broadcast.editor.errors.senderDkimError'));
                } else {
                    this.errorAdd(this.$t(`email.broadcast.editor.errors.step.${invalidStep.title.toLowerCase()}`));
                }

                if (invalidStep.title !== STEPS.CONTENT) {
                    this.activateStep(invalidStep);
                }

                return false;
            }

            if (this.isAnyStepActive) {
                this.setStepStatusByTitle(this.activeStepTitle, STATUSES.ERROR_HAS_NOT_FINISHED_EDITING);
                this.errorAdd(this.$t('email.broadcast.editor.errors.hasNotFinishedEditing'));

                return false;
            }

            return true;
        },

        validateBroadcastState() {
            if (this.email.broadcastState === BROADCAST_STATUS.DONE) {
                this.$toast({ message: this.$t('email.broadcast.editor.errors.cannotModify') });
                this.$emit('back');
            }
        },

        handleUnlayerWizardModalClose() {
            if (this.errors.length > 0) {
                this.errorRemoveAll();
                this.validate();
            }

            this.isUnlayerWizardOpen = false;
        },

        async fetchMergeFields() {
            try {
                this.mergeFields = await getMergeFields();
            } catch (error) {
                this.mergeFields = [];
            }
        },

        async recalculateRecipientStatistics() {
            const recalculatedStatistics = await recalculateRecipientStatistics(this.broadcastId);

            this.email[STEPS.AUDIENCE].recipientStatistics = {
                totalRecipients: recalculatedStatistics.totalRecipients,
                eligibleRecipients: recalculatedStatistics.eligibleRecipients,
                ineligibleRecipients: recalculatedStatistics.ineligibleRecipients,
                duplicateRecipients: recalculatedStatistics.duplicateRecipients,
                throttled: recalculatedStatistics.throttled,
            };
        },

        async loadBroadcastStatus() {
            if (!this.coreConfig.isPreventSendingAlreadySentOrScheduledEnabled) {
                 return;
            }

            const { broadcastId } = this;

            try {
                if (this.email.broadcastState?.toLowerCase() === BROADCAST_STATUS.DRAFT?.toLowerCase()) {
                    this.email = await getUnlayerBroadcastWithTemplateById(broadcastId);
                }
            this.isBroadcastNonEditable = this.email.broadcastState?.toLowerCase() !== BROADCAST_STATUS.DRAFT?.toLowerCase();
            } catch (err) {
                // do nothing
            }
        },

        closeNonEditableDialog() {
            this.isBroadcastNonEditable = false;
            this.$emit('back');
        },
    },
};
</script>

<style lang="scss" scoped>
$stage-icon-size: px-to-rem(26px);

.page-content {
    max-width: px-to-rem(1024);
    margin: $spacing-200 auto;
}

.broadcast-load-error-container {
    display: flex;
    min-height: px-to-rem(300px);
    justify-content: center;
    align-items: center;
}

.broadcast-steps {
    flex: 1;
    margin: $spacing-200 auto $spacing-400;
    border-radius: $border-radius;
    border: 1px solid $color-gray-200;

    @media($small) {
        border: 0;
    }
}

.broadcast-step {
    padding: $spacing-200;
    --input-font-size: #{$font-size-sm};

    &:not(:last-child) {
        border-bottom: 1px solid $color-gray-200;
    }
}

.error-container {
    display: flex;
    align-items: flex-start;
    flex-direction: column;
    margin: 0 auto;

    @media($small) {
        margin: 0 $spacing-200;
    }

    > *:not(:first-child) {
        margin-top: $spacing-200;
    }
}

.step-status-icon {
    @include circle($stage-icon-size);
    border: solid 2px $color-gray-200;
    background-color: $color-paper;
    height: $stage-icon-size;
    width: $stage-icon-size;
    color: $color-gray-200;
    @include margin-end($spacing-100);
    --icon-size: #{$icon-size-small};

    &.active {
        border-color: $color-blue;
        color: $color-blue;
    }

    &.error {
        border: none;
        color: $color-red;

        --icon-color: #{$color-red};
        --icon-size: #{$stage-icon-size};
    }

    &.complete {
        background-color: $color-blue;
        border-color: $color-blue;

        --icon-color: #{$color-paper};
        --icon-size: #{$icon-size-small};
    }
}
.step-title {
    display: flex;
    align-items: center;
    font-size: $font-size-lg;
    font-weight: $font-weight-semibold;
    width: px-to-rem(220);
}
.save {
    @include margin-end($spacing-100);
}

.step-action {
    justify-self: flex-end;
}
</style>

<style lang="scss">
    div.email-broadcast-editor-page {
        div.modal-header{
            padding: $spacing-100;
        }

        div.modal-body {
            padding: 0;
        }
    }
</style>
